import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';

import {
  ChoiceOption,
  RichContentBlock
} from '@core/services/ai/ai-assistant.service';
import { AiSummaryBlockComponent } from './blocks/ai-summary-block.component';
import { AiEntityMatchBlockComponent } from './blocks/ai-entity-match-block.component';
import { AiEntitySummaryBlockComponent } from './blocks/ai-entity-summary-block.component';
import { AiChoiceBlockComponent } from './blocks/ai-choice-block.component';
import { AiFormFieldsBlockComponent } from './blocks/ai-form-fields-block.component';
import { AiKpiGridBlockComponent } from './blocks/ai-kpi-grid-block.component';
import { AiEntityListBlockComponent } from './blocks/ai-entity-list-block.component';
import { AiDataTableBlockComponent, DataTableRowAction } from './blocks/ai-data-table-block.component';
import { AiTimelineBlockComponent } from './blocks/ai-timeline-block.component';
import { AiQuestionBlockComponent } from './blocks/ai-question-block.component';
import { AiBarChartBlockComponent } from './blocks/ai-bar-chart-block.component';
import { AiDonutChartBlockComponent } from './blocks/ai-donut-chart-block.component';
import { AiStatusTrackerBlockComponent } from './blocks/ai-status-tracker-block.component';
import { AiAlertBlockComponent } from './blocks/ai-alert-block.component';
import { AiSpecListBlockComponent } from './blocks/ai-spec-list-block.component';
import { AiMetricCardBlockComponent } from './blocks/ai-metric-card-block.component';
import { AiStatComparisonBlockComponent } from './blocks/ai-stat-comparison-block.component';
import { AiEntityPickBlockComponent } from './blocks/ai-entity-pick-block.component';
import { AiQuoteBlockComponent } from './blocks/ai-quote-block.component';
import { AiProgressBarBlockComponent } from './blocks/ai-progress-bar-block.component';
import { AiAddressCardBlockComponent } from './blocks/ai-address-card-block.component';
import { AiCodeBlockComponent } from './blocks/ai-code-block.component';
import { AiNavigateActionBlockComponent } from './blocks/ai-navigate-action-block.component';
import { AiNavigateListBlockComponent } from './blocks/ai-navigate-list-block.component';

/**
 * Dispatcher that renders a `RichContentBlock` envelope to its concrete
 * component. Add a new block type by:
 *   1. Extending `RichContentBlock` in `ai-assistant.service.ts`
 *   2. Adding the matching backend type in `tools/types.ts`
 *   3. Creating a presentational component under `./blocks/`
 *   4. Adding a branch in this template
 *   5. Wiring tools to call `ctx.emitRichContent({ kind: ... })`
 *
 * Falls back to nothing for unknown kinds — the assistant message's
 * markdown body always remains as the textual fallback.
 */
@Component({
  selector: 'app-ai-block-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiSummaryBlockComponent,
    AiEntityMatchBlockComponent,
    AiEntitySummaryBlockComponent,
    AiChoiceBlockComponent,
    AiFormFieldsBlockComponent,
    AiKpiGridBlockComponent,
    AiEntityListBlockComponent,
    AiDataTableBlockComponent,
    AiTimelineBlockComponent,
    AiQuestionBlockComponent,
    AiBarChartBlockComponent,
    AiDonutChartBlockComponent,
    AiStatusTrackerBlockComponent,
    AiAlertBlockComponent,
    AiSpecListBlockComponent,
    AiMetricCardBlockComponent,
    AiStatComparisonBlockComponent,
    AiEntityPickBlockComponent,
    AiQuoteBlockComponent,
    AiProgressBarBlockComponent,
    AiAddressCardBlockComponent,
    AiCodeBlockComponent,
    AiNavigateActionBlockComponent,
    AiNavigateListBlockComponent
  ],
  template: `
    @switch (block.kind) {
      @case ('summary') {
        <app-ai-summary-block [block]="$any(block)"></app-ai-summary-block>
      }
      @case ('entity-match') {
        <app-ai-entity-match-block
          [block]="$any(block)"></app-ai-entity-match-block>
      }
      @case ('entity-summary') {
        <app-ai-entity-summary-block
          [block]="$any(block)"></app-ai-entity-summary-block>
      }
      @case ('choice') {
        <app-ai-choice-block
          [block]="$any(block)"
          (choiceSelected)="choiceSelected.emit($event)">
        </app-ai-choice-block>
      }
      @case ('form-fields') {
        <app-ai-form-fields-block
          [block]="$any(block)"
          (formSubmitted)="formSubmitted.emit($event)">
        </app-ai-form-fields-block>
      }
      @case ('kpi-grid') {
        <app-ai-kpi-grid-block [block]="$any(block)"></app-ai-kpi-grid-block>
      }
      @case ('entity-list') {
        <app-ai-entity-list-block
          [block]="$any(block)"></app-ai-entity-list-block>
      }
      @case ('data-table') {
        <app-ai-data-table-block
          [block]="$any(block)"
          (rowAction)="onDataTableRowAction($event)">
        </app-ai-data-table-block>
      }
      @case ('timeline') {
        <app-ai-timeline-block [block]="$any(block)"></app-ai-timeline-block>
      }
      @case ('question') {
        <app-ai-question-block
          [block]="$any(block)"
          (answered)="questionAnswered.emit($event)">
        </app-ai-question-block>
      }
      @case ('bar-chart') {
        <app-ai-bar-chart-block [block]="$any(block)"></app-ai-bar-chart-block>
      }
      @case ('donut-chart') {
        <app-ai-donut-chart-block
          [block]="$any(block)"></app-ai-donut-chart-block>
      }
      @case ('status-tracker') {
        <app-ai-status-tracker-block
          [block]="$any(block)"></app-ai-status-tracker-block>
      }
      @case ('alert') {
        <app-ai-alert-block
          [block]="$any(block)"
          (actionInvoked)="messageRequested.emit($event)">
        </app-ai-alert-block>
      }
      @case ('spec-list') {
        <app-ai-spec-list-block [block]="$any(block)"></app-ai-spec-list-block>
      }
      @case ('metric-card') {
        <app-ai-metric-card-block
          [block]="$any(block)"></app-ai-metric-card-block>
      }
      @case ('stat-comparison') {
        <app-ai-stat-comparison-block
          [block]="$any(block)"></app-ai-stat-comparison-block>
      }
      @case ('entity-pick') {
        <app-ai-entity-pick-block
          [block]="$any(block)"
          (picked)="messageRequested.emit($event)">
        </app-ai-entity-pick-block>
      }
      @case ('quote') {
        <app-ai-quote-block [block]="$any(block)"></app-ai-quote-block>
      }
      @case ('progress-bar') {
        <app-ai-progress-bar-block
          [block]="$any(block)"></app-ai-progress-bar-block>
      }
      @case ('address-card') {
        <app-ai-address-card-block
          [block]="$any(block)"></app-ai-address-card-block>
      }
      @case ('code-block') {
        <app-ai-code-block [block]="$any(block)"></app-ai-code-block>
      }
      @case ('navigate-action') {
        <app-ai-navigate-action-block
          [block]="$any(block)"></app-ai-navigate-action-block>
      }
      @case ('navigate-list') {
        <app-ai-navigate-list-block
          [block]="$any(block)"></app-ai-navigate-list-block>
      }
      @default {
        <!-- Unknown kind: render nothing ON PURPOSE. The message's
             markdown body remains the textual fallback, and the service
             already logs dropped/unknown blocks (rich-content.guard). -->
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }
    `
  ]
})
export class AiBlockHostComponent {
  @Input({ required: true }) block!: RichContentBlock;
  @Output() readonly choiceSelected = new EventEmitter<ChoiceOption>();
  @Output() readonly formSubmitted = new EventEmitter<{
    formId: string;
    toolName?: string;
    values: Record<string, unknown>;
  }>();
  @Output() readonly questionAnswered = new EventEmitter<string>();
  /**
   * Generic "send this text as a follow-up user message" event used by
   * the alert action button and the entity-pick row click. The host
   * funnels both into one signal so panels only wire one handler.
   */
  @Output() readonly messageRequested = new EventEmitter<string>();
  /** Row CTAs from data-table (e.g. stage payment reminder). */
  @Output() readonly dataTableRowAction = new EventEmitter<DataTableRowAction>();

  onDataTableRowAction(action: DataTableRowAction): void {
    this.dataTableRowAction.emit(action);
  }
}
